import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Button,
    Paper,
    Grid,
    Box,
    Card,
    CardContent,
    CardActions,
    useTheme,
    Avatar,
    Alert,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    CircularProgress,
    Chip,
    Fade,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import {
    Storage as DatabaseIcon,
    Cached,
    ClearAll,
    Visibility,
    Sync,
    Settings,
    Speed,
    Error as ErrorIcon,
    Warning,
    Refresh,
    DeleteSweep,
    Build,
    DataUsage,
    CheckCircle,
    HourglassEmpty,
    ExpandMore,
    PendingActions,
    History,
    Schedule
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import useCustomToast from '../../hooks/useToast';
import {
    APIConfigCache,
    APIClearCache,
    APIViewClear,
    APIRouteCache,
    APIClearAll,
    APIMigrate
} from '../../apiServices/APIs';
import { getUserDetails } from '../../utils/helpers';
import { apiService } from '../../apiServices/ApiService';
import { usePermission } from '../../hooks/usePermission';
import LoadingState from '../../components/LoadingState';

const Database = () => {
    const theme = useTheme();
    const showToast = useCustomToast();
    const [processing, setProcessing] = useState(false);
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [selectedAction, setSelectedAction] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [operationResult, setOperationResult] = useState(null);
    const [migrationDetails, setMigrationDetails] = useState(null);
    const [showMigrationDetails, setShowMigrationDetails] = useState(false);
    const [migrationStatus, setMigrationStatus] = useState(null);
    const [loadingStatus, setLoadingStatus] = useState(false);

    const { loading: permissionLoading } = usePermission();

    // Fetch migration status on component mount
    useEffect(() => {
        fetchMigrationStatus();
    }, []);

    const fetchMigrationStatus = async () => {
        setLoadingStatus(true);
        try {
            const userBranchId = getUserDetails("branchId");
            // Changed apiService.list to apiService.listStats to preserve object-level response structure
            const response = await apiService.listStats(userBranchId, `${APIMigrate}/status`);
            
            if (response && response.success !== false) {
                setMigrationStatus(response.data);
            }
        } catch (error) {
            console.error('Error fetching migration status:', error);
        } finally {
            setLoadingStatus(false);
        }
    };

    // Cache Management Actions
    const cacheActions = [
        {
            id: 'config-cache',
            title: 'Config Cache',
            description: 'Clear and rebuild configuration cache',
            icon: <Settings />,
            color: '#1976d2',
            endpoint: APIConfigCache,
            confirmMessage: 'This will clear and rebuild the configuration cache. Continue?'
        },
        {
            id: 'clear-cache',
            title: 'Clear Cache',
            description: 'Clear application cache data',
            icon: <Cached />,
            color: '#f57c00',
            endpoint: APIClearCache,
            confirmMessage: 'This will clear all application cache data. Continue?'
        },
        {
            id: 'view-clear',
            title: 'Clear View Cache',
            description: 'Clear compiled view templates',
            icon: <Visibility />,
            color: '#7b1fa2',
            endpoint: APIViewClear,
            confirmMessage: 'This will clear all compiled view templates. Continue?'
        },
        {
            id: 'route-cache',
            title: 'Clear Route Cache',
            description: 'Clear and rebuild route cache',
            icon: <Sync />,
            color: '#2e7d32',
            endpoint: APIRouteCache,
            confirmMessage: 'This will clear and rebuild the route cache. Continue?'
        }
    ];

    // Database Actions
    const dbActions = [
        {
            id: 'migrate',
            title: 'Run Migrations',
            description: 'Run pending database migrations only if new ones exist',
            icon: <DataUsage />,
            color: '#d32f2f',
            endpoint: APIMigrate,
            confirmMessage: 'This will check for and run any pending database migrations. Continue?',
            danger: true
        }
    ];

    // System Actions
    const systemActions = [
        {
            id: 'clear-all',
            title: 'Clear All Caches',
            description: 'Clear all system caches at once',
            icon: <ClearAll />,
            color: '#c62828',
            endpoint: APIClearAll,
            confirmMessage: 'This will clear all system caches (config, cache, view, route). Continue?',
            danger: true
        }
    ];

    const handleActionClick = (action) => {
        setSelectedAction(action);
        setOpenConfirmDialog(true);
    };

    const handleExecuteAction = async () => {
        if (!selectedAction) return;

        setProcessing(true);
        setOpenConfirmDialog(false);
        setMigrationDetails(null);
        setShowMigrationDetails(false);

        try {
            const userBranchId = getUserDetails("branchId");
            // Changed apiService.list to apiService.listStats to preserve object-level response structure
            const result = await apiService.listStats(userBranchId, selectedAction.endpoint);

            if (result && result.success !== false) {
                // Handle migration-specific results
                if (selectedAction.id === 'migrate' && result.data) {
                    setMigrationDetails(result.data);
                    setShowMigrationDetails(true);
                    
                    // Refresh migration status after execution
                    await fetchMigrationStatus();
                    
                    // Custom message based on whether migrations ran or not
                    if (result.data.new_migrations === 0) {
                        showToast("Info", "No new migrations to run. Database is up to date.", "info");
                        setOperationResult({
                            success: true,
                            message: 'No new migrations found. Database is up to date!',
                            type: 'info'
                        });
                    } else {
                        showToast("Success", `Successfully ran ${result.data.new_migrations} migration(s)!`, "success");
                        setOperationResult({
                            success: true,
                            message: `Successfully executed ${result.data.new_migrations} migration(s)!`,
                            type: 'success'
                        });
                    }
                } else {
                    setOperationResult({
                        success: true,
                        message: result.message || `${selectedAction.title} completed successfully!`,
                        type: 'success'
                    });
                    showToast("Success", result.message || `${selectedAction.title} completed successfully!`, "success");
                }
            } else {
                throw new Error(result.message || `Failed to execute ${selectedAction.title}`);
            }
        } catch (error) {
            console.error("Action error:", error);
            setOperationResult({
                success: false,
                message: error.message || `Failed to execute ${selectedAction.title}`,
                type: 'error'
            });
            showToast("Error", error.message || `Failed to execute ${selectedAction.title}`, "error");
        } finally {
            setProcessing(false);
            setSelectedAction(null);
            setTimeout(() => {
                setOperationResult(null);
                setShowMigrationDetails(false);
            }, 15000);
        }
    };

    const handleCloseConfirm = () => {
        setOpenConfirmDialog(false);
        setSelectedAction(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Migration Status Panel Component
    const MigrationStatusPanel = () => {
        if (loadingStatus) {
            return (
                <Card sx={{ mb: 3, borderRadius: 3 }}>
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" sx={{ mt: 1 }}>Loading migration status...</Typography>
                    </CardContent>
                </Card>
            );
        }

        if (!migrationStatus) return null;

        const pendingCount = migrationStatus.total_pending || 0;
        const ranCount = migrationStatus.total_ran || 0;

        return (
            <Card sx={{ mb: 3, borderRadius: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Schedule color="primary" />
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                Migration Status
                            </Typography>
                        </Box>
                        <Button
                            size="small"
                            startIcon={<Refresh />}
                            onClick={fetchMigrationStatus}
                            disabled={loadingStatus}
                        >
                            Refresh
                        </Button>
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ bgcolor: theme.palette.info.light, color: 'white' }}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2">Total Migrations</Typography>
                                    <Typography sx={{ fontWeight: 'bold' }}>
                                        {pendingCount + ranCount}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ bgcolor: theme.palette.success.light, color: 'white' }}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2">Completed</Typography>
                                    <Typography sx={{ fontWeight: 'bold' }}>
                                        {ranCount}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                bgcolor: pendingCount > 0 ? theme.palette.warning.light : theme.palette.success.light, 
                                color: 'white' 
                            }}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2">Pending</Typography>
                                    <Typography sx={{ fontWeight: 'bold' }}>
                                        {pendingCount}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Card sx={{ 
                                bgcolor: pendingCount > 0 ? theme.palette.error.light : theme.palette.success.light, 
                                color: 'white' 
                            }}>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="body2">Status</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        {pendingCount > 0 ? 'Action Needed' : 'Up to Date'}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Pending Migrations List */}
                    {pendingCount > 0 && migrationStatus.pending_migrations && (
                        <Box sx={{ mt: 2 }}>
                            <Alert severity="warning" icon={<PendingActions />}>
                                There {pendingCount === 1 ? 'is' : 'are'} {pendingCount} pending migration{pendingCount !== 1 ? 's' : ''} that need to be run.
                            </Alert>
                            <Accordion sx={{ mt: 1 }}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography fontWeight="bold" color="warning.main">
                                        Pending Migrations ({pendingCount})
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Migration Name</TableCell>
                                                    <TableCell>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {migrationStatus.pending_migrations.map((migration, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell>
                                                            <Typography variant="body2" fontFamily="monospace">
                                                                {migration.name}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip 
                                                                label="Pending" 
                                                                size="small" 
                                                                color="warning" 
                                                                icon={<HourglassEmpty />}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </AccordionDetails>
                            </Accordion>
                        </Box>
                    )}

                    {/* Completed Migrations */}
                    {ranCount > 0 && migrationStatus.ran_migrations && (
                        <Accordion sx={{ mt: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography fontWeight="bold" color="success.main">
                                    Completed Migrations ({ranCount})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Migration Name</TableCell>
                                                <TableCell>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {migrationStatus.ran_migrations.map((migration, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <Typography variant="body2" fontFamily="monospace">
                                                            {migration.name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            label="Completed" 
                                                            size="small" 
                                                            color="success" 
                                                            icon={<CheckCircle />}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </CardContent>
            </Card>
        );
    };

    // Migration Results Component
    const MigrationResultsView = ({ details }) => {
        if (!details) return null;

        return (
            <Box sx={{ mt: 2 }}>
                <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <History color="primary" />
                        Migration Results
                    </Typography>
                    
                    {details.new_migrations === 0 ? (
                        <Alert severity="info" icon={<CheckCircle />} sx={{ mt: 1 }}>
                            <Typography variant="body2" fontWeight="bold">
                                All migrations are up to date. No new migrations were needed.
                            </Typography>
                            <Typography variant="caption">
                                Total migrations in database: {details.total_migrations || 0}
                            </Typography>
                        </Alert>
                    ) : (
                        <>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                Successfully executed {details.new_migrations} migration(s)!
                            </Alert>
                            
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={6} sm={3}>
                                    <Card sx={{ bgcolor: theme.palette.success.light, color: 'white' }}>
                                        <CardContent>
                                            <Typography variant="caption">Total Migrations</Typography>
                                            <Typography variant="h5">{details.total_migrations || 0}</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Card sx={{ bgcolor: theme.palette.info.light, color: 'white' }}>
                                        <CardContent>
                                            <Typography variant="caption">Newly Executed</Typography>
                                            <Typography variant="h5">{details.new_migrations || 0}</Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography fontWeight="bold">
                                        Executed Migrations ({details.migrations_run?.length || 0})
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <List dense>
                                        {details.migrations_run?.map((migration, index) => (
                                            <ListItem key={index}>
                                                <ListItemIcon>
                                                    <CheckCircle color="success" fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText 
                                                    primary={migration}
                                                    primaryTypographyProps={{ 
                                                        variant: 'body2',
                                                        fontFamily: 'monospace'
                                                    }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </AccordionDetails>
                            </Accordion>

                            {details.executed_details && details.executed_details.length > 0 && (
                                <Accordion sx={{ mt: 1 }}>
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography fontWeight="bold">
                                            Execution Details
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box 
                                            sx={{ 
                                                bgcolor: 'grey.900', 
                                                color: 'grey.100',
                                                p: 2, 
                                                borderRadius: 1,
                                                fontFamily: 'monospace',
                                                fontSize: '0.75rem',
                                                whiteSpace: 'pre-wrap',
                                                maxHeight: 300,
                                                overflow: 'auto'
                                            }}
                                        >
                                            {details.executed_details.map((detail, index) => (
                                                <Box key={index} sx={{ mb: 0.5 }}>
                                                    {detail}
                                                </Box>
                                            ))}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            )}
                        </>
                    )}
                </Paper>
            </Box>
        );
    };

    if (permissionLoading) {
        return <LoadingState message="Loading permissions..." variant="medical" size="large" fullPage />;
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
                <DatabaseIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mr: 2 }} />
                <Box>
                    <Typography sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                        Database & Cache Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage your application's database migrations and cache systems
                    </Typography>
                </Box>
            </Box>

            {/* Migration Status Panel */}
            <MigrationStatusPanel />

            {/* Quick Stats */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: 3, height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Cache Actions</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                        {cacheActions.length}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.primary.light, width: 48, height: 48 }}>
                                    <Cached sx={{ fontSize: 28 }} />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: 3, height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Database Actions</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                        {dbActions.length}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.success.light, width: 48, height: 48 }}>
                                    <DataUsage sx={{ fontSize: 28 }} />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: 3, height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">System Actions</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                        {systemActions.length}
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.warning.light, width: 48, height: 48 }}>
                                    <Settings sx={{ fontSize: 28 }} />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: 3, height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Status</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                                        Ready
                                    </Typography>
                                </Box>
                                <Avatar sx={{ bgcolor: theme.palette.info.light, width: 48, height: 48 }}>
                                    <Speed sx={{ fontSize: 28 }} />
                                </Avatar>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Operation Result Alert */}
            {operationResult && (
                <Fade in={!!operationResult}>
                    <Box sx={{ mb: 3 }}>
                        <Alert 
                            severity={operationResult.type || (operationResult.success ? 'success' : 'error')}
                            sx={{ borderRadius: 2 }}
                            onClose={() => {
                                setOperationResult(null);
                                setShowMigrationDetails(false);
                            }}
                        >
                            {operationResult.message}
                        </Alert>
                        
                        {/* Show migration details if available */}
                        {showMigrationDetails && migrationDetails && (
                            <MigrationResultsView details={migrationDetails} />
                        )}
                    </Box>
                </Fade>
            )}

            {/* Cache Management Section */}
            <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Cached sx={{ mr: 1, color: theme.palette.primary.main }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Cache Management
                    </Typography>
                    <Chip 
                        label={`${cacheActions.length} actions`} 
                        size="small" 
                        color="primary" 
                        sx={{ ml: 2 }}
                    />
                </Box>

                <Grid container spacing={3}>
                    {cacheActions.map((action, index) => (
                        <Grid item xs={12} sm={6} md={3} key={action.id}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <Card 
                                    sx={{ 
                                        height: '100%', 
                                        borderRadius: 3,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: theme.shadows[8]
                                        }
                                    }}
                                >
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Avatar sx={{ bgcolor: `${action.color}20`, color: action.color, mr: 1.5 }}>
                                                {action.icon}
                                            </Avatar>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {action.title}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">
                                            {action.description}
                                        </Typography>
                                    </CardContent>
                                    <CardActions sx={{ p: 2, pt: 0 }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            size="small"
                                            sx={{
                                                backgroundColor: action.color,
                                                '&:hover': {
                                                    backgroundColor: action.color,
                                                    opacity: 0.8
                                                }
                                            }}
                                            startIcon={<Refresh />}
                                            onClick={() => handleActionClick(action)}
                                            disabled={processing}
                                        >
                                            Execute
                                        </Button>
                                    </CardActions>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            {/* Database & System Maintenance Section (Combined Card) */}
            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Settings sx={{ mr: 1, color: theme.palette.error.main }} />
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Database & System Administration
                    </Typography>
                    <Chip 
                        label={`${dbActions.length + systemActions.length} actions`} 
                        size="small" 
                        color="error" 
                        sx={{ ml: 2 }}
                    />
                </Box>

                <Grid container spacing={3}>
                    {/* Database Management Actions */}
                    {dbActions.map((action, index) => (
                        <Grid item xs={12} sm={6} md={3} key={action.id}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <Card 
                                    sx={{ 
                                        height: '100%', 
                                        borderRadius: 3,
                                        border: action.danger ? `2px solid ${theme.palette.error.main}` : 'none',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: theme.shadows[8]
                                        }
                                    }}
                                >
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Avatar sx={{ bgcolor: `${action.color}20`, color: action.color, mr: 1.5 }}>
                                                {action.icon}
                                            </Avatar>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {action.title}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">
                                            {action.description}
                                        </Typography>
                                        {action.danger && (
                                            <Chip 
                                                label="Warning" 
                                                size="small" 
                                                color="error" 
                                                variant="outlined"
                                                sx={{ mt: 1 }}
                                            />
                                        )}
                                    </CardContent>
                                    <CardActions sx={{ p: 2, pt: 0 }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            size="small"
                                            color={action.danger ? 'error' : 'primary'}
                                            startIcon={<Build />}
                                            onClick={() => handleActionClick(action)}
                                            disabled={processing}
                                        >
                                            Execute
                                        </Button>
                                    </CardActions>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}

                    {/* System Maintenance Actions */}
                    {systemActions.map((action, index) => (
                        <Grid item xs={12} sm={6} md={3} key={action.id}>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: (dbActions.length + index) * 0.1 }}
                            >
                                <Card 
                                    sx={{ 
                                        height: '100%', 
                                        borderRadius: 3,
                                        border: `2px solid ${theme.palette.error.main}`,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: theme.shadows[8]
                                        }
                                    }}
                                >
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                            <Avatar sx={{ bgcolor: `${action.color}20`, color: action.color, mr: 1.5 }}>
                                                {action.icon}
                                            </Avatar>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {action.title}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">
                                            {action.description}
                                        </Typography>
                                        <Chip 
                                            label="Dangerous" 
                                            size="small" 
                                            color="error" 
                                            variant="outlined"
                                            sx={{ mt: 1 }}
                                        />
                                    </CardContent>
                                    <CardActions sx={{ p: 2, pt: 0 }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            size="small"
                                            color="error"
                                            startIcon={<DeleteSweep />}
                                            onClick={() => handleActionClick(action)}
                                            disabled={processing}
                                        >
                                            Execute
                                        </Button>
                                    </CardActions>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            {/* Confirmation Dialog */}
            <Dialog
                open={openConfirmDialog}
                onClose={handleCloseConfirm}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Warning color="warning" />
                    Confirm Action
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {selectedAction?.confirmMessage || 'Are you sure you want to proceed with this action?'}
                    </DialogContentText>
                    {selectedAction?.danger && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            <Typography variant="body2" fontWeight="bold">
                                Warning: This action cannot be undone. Please ensure you have a backup.
                            </Typography>
                        </Alert>
                    )}
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Action:</strong> {selectedAction?.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Description:</strong> {selectedAction?.description}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={handleCloseConfirm} 
                        variant="outlined"
                        disabled={processing}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleExecuteAction}
                        variant="contained"
                        color={selectedAction?.danger ? 'error' : 'primary'}
                        disabled={processing}
                        startIcon={processing ? <CircularProgress size={20} /> : null}
                    >
                        {processing ? 'Processing...' : 'Confirm & Execute'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Processing Overlay */}
            {processing && (
                <Box sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999 }}>
                    <Paper sx={{ p: 2, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2">
                            {selectedAction?.id === 'migrate' 
                                ? 'Checking and running migrations...' 
                                : `Executing ${selectedAction?.title}...`}
                        </Typography>
                    </Paper>
                </Box>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert 
                    severity={snackbar.severity} 
                    onClose={handleCloseSnackbar}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default Database;